import { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Progress } from '@/components/ui/progress';
import {
  Play, Loader2, CheckCircle2, AlertCircle, Clock, Trash2, 
  ListOrdered, BarChart3, X, Layers
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BatchAnalysisPanel({ projects, onClose, onQueueUpdated }) {
  const [selectedIds, setSelectedIds] = useState([]);
  const [queue, setQueue] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('select'); // select, queue, history

  const fetchQueue = useCallback(async () => {
    try {
      const res = await fetch(`${API}/batch/queue`, { credentials: 'include' });
      if (res.ok) setQueue(await res.json());
    } catch (e) {}
  }, []);

  const fetchHistory = useCallback(async () => {
    try {
      const res = await fetch(`${API}/batch/history?limit=20`, { credentials: 'include' });
      if (res.ok) setHistory(await res.json());
    } catch (e) {}
  }, []);

  useEffect(() => {
    fetchQueue();
    fetchHistory();
    // Poll queue status
    const interval = setInterval(fetchQueue, 5000);
    return () => clearInterval(interval);
  }, [fetchQueue, fetchHistory]);

  const toggleProject = (projectId) => {
    setSelectedIds(prev => 
      prev.includes(projectId) 
        ? prev.filter(id => id !== projectId)
        : [...prev, projectId]
    );
  };

  const selectAll = () => {
    const eligibleIds = projects
      .filter(p => p.status !== 'analyzing' && p.file_count > 0)
      .map(p => p.project_id);
    setSelectedIds(eligibleIds);
  };

  const deselectAll = () => setSelectedIds([]);

  const queueSelected = async () => {
    if (selectedIds.length === 0) return;
    setLoading(true);
    try {
      const res = await fetch(`${API}/batch/queue`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ project_ids: selectedIds })
      });
      if (res.ok) {
        setSelectedIds([]);
        setActiveTab('queue');
        fetchQueue();
        onQueueUpdated?.();
      }
    } catch (e) {}
    finally { setLoading(false); }
  };

  const analyzeAllNow = async () => {
    if (selectedIds.length === 0) return;
    setLoading(true);
    try {
      const res = await fetch(`${API}/batch-analyze`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ project_ids: selectedIds })
      });
      if (res.ok) {
        const data = await res.json();
        setSelectedIds([]);
        onQueueUpdated?.();
        onClose?.();
      }
    } catch (e) {}
    finally { setLoading(false); }
  };

  const removeFromQueue = async (queueId) => {
    try {
      await fetch(`${API}/batch/queue/${queueId}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      fetchQueue();
    } catch (e) {}
  };

  const statusConfig = {
    queued: { label: 'Queued', color: 'bg-[#F0F4F7] text-[#446E87]', icon: Clock },
    processing: { label: 'Processing', color: 'bg-[#72A4BF]/10 text-[#0D1830]', icon: Loader2 },
    completed: { label: 'Completed', color: 'bg-[#E8F5E9] text-[#2E7D32]', icon: CheckCircle2 },
    error: { label: 'Error', color: 'bg-[#FFEBEE] text-[#C62828]', icon: AlertCircle },
  };

  const processingCount = queue.filter(q => q.status === 'processing').length;
  const queuedCount = queue.filter(q => q.status === 'queued').length;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="batch-panel">
      <Card className="w-full max-w-3xl max-h-[80vh] flex flex-col bg-white border-[#E2E8ED] shadow-xl">
        <CardHeader className="border-b border-[#E2E8ED] pb-3 shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-[#72A4BF]/10 flex items-center justify-center">
                <Layers className="w-5 h-5 text-[#0D1830]" />
              </div>
              <div>
                <CardTitle className="text-lg">Batch Analysis</CardTitle>
                <p className="text-xs text-[#72A4BF]">Queue multiple projects for background analysis</p>
              </div>
            </div>
            <button onClick={onClose} className="p-2 rounded-lg hover:bg-[#72A4BF]/8">
              <X className="w-5 h-5 text-[#446E87]" />
            </button>
          </div>
          
          {/* Tab Navigation */}
          <div className="flex gap-1 mt-4 bg-[#F0F4F7] p-1 rounded-lg">
            <button
              onClick={() => setActiveTab('select')}
              className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
                activeTab === 'select' ? 'bg-white text-[#0D1830] shadow-sm' : 'text-[#446E87] hover:text-[#0D1830]'
              }`}
            >
              Select Projects
            </button>
            <button
              onClick={() => setActiveTab('queue')}
              className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors flex items-center justify-center gap-2 ${
                activeTab === 'queue' ? 'bg-white text-[#0D1830] shadow-sm' : 'text-[#446E87] hover:text-[#0D1830]'
              }`}
            >
              Queue
              {(processingCount + queuedCount) > 0 && (
                <Badge className="bg-[#1D4053] text-[#0D1830] border-0 text-[10px]">
                  {processingCount + queuedCount}
                </Badge>
              )}
            </button>
            <button
              onClick={() => { setActiveTab('history'); fetchHistory(); }}
              className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
                activeTab === 'history' ? 'bg-white text-[#0D1830] shadow-sm' : 'text-[#446E87] hover:text-[#0D1830]'
              }`}
            >
              History
            </button>
          </div>
        </CardHeader>

        <CardContent className="flex-1 overflow-y-auto p-4">
          {/* SELECT TAB */}
          {activeTab === 'select' && (
            <div>
              <div className="flex items-center justify-between mb-3">
                <p className="text-sm text-[#446E87]">
                  {selectedIds.length} of {projects.filter(p => p.file_count > 0).length} projects selected
                </p>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={selectAll} className="text-xs h-7">
                    Select All
                  </Button>
                  <Button variant="outline" size="sm" onClick={deselectAll} className="text-xs h-7">
                    Clear
                  </Button>
                </div>
              </div>
              
              <div className="space-y-2 max-h-[300px] overflow-y-auto">
                {projects.filter(p => p.file_count > 0).map((project) => {
                  const isSelected = selectedIds.includes(project.project_id);
                  const isAnalyzing = project.status === 'analyzing';
                  const isQueued = queue.some(q => q.project_id === project.project_id && ['queued', 'processing'].includes(q.status));
                  
                  return (
                    <div
                      key={project.project_id}
                      onClick={() => !isAnalyzing && !isQueued && toggleProject(project.project_id)}
                      className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                        isSelected ? 'border-[#1D4053] bg-[#72A4BF]/10' : 'border-[#E2E8ED] hover:border-[#1D4053]/50'
                      } ${(isAnalyzing || isQueued) ? 'opacity-50 cursor-not-allowed' : ''}`}
                      data-testid={`batch-project-${project.project_id}`}
                    >
                      <Checkbox 
                        checked={isSelected} 
                        disabled={isAnalyzing || isQueued}
                        className="data-[state=checked]:bg-[#1D4053] data-[state=checked]:border-[#1D4053]"
                      />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-[#0D1830] truncate">{project.name}</p>
                        <p className="text-xs text-[#72A4BF]">{project.file_count} files</p>
                      </div>
                      {isAnalyzing && (
                        <Badge className="bg-[#72A4BF]/10 text-[#0D1830] border-0 text-[10px]">
                          <Loader2 className="w-3 h-3 mr-1 animate-spin" />Analyzing
                        </Badge>
                      )}
                      {isQueued && (
                        <Badge className="bg-[#F0F4F7] text-[#446E87] border-0 text-[10px]">
                          <Clock className="w-3 h-3 mr-1" />In Queue
                        </Badge>
                      )}
                    </div>
                  );
                })}
              </div>

              {selectedIds.length > 0 && (
                <div className="mt-4 pt-4 border-t border-[#E2E8ED]">
                  <Button 
                    onClick={queueSelected} 
                    disabled={loading}
                    className="w-full bg-[#0D1830] text-white rounded-full font-semibold hover:opacity-90"
                    data-testid="queue-selected-btn"
                  >
                    {loading ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Play className="w-4 h-4 mr-2" />
                    )}
                    Queue {selectedIds.length} Project{selectedIds.length > 1 ? 's' : ''} for Analysis
                  </Button>
                  <Button
                    onClick={analyzeAllNow}
                    disabled={loading}
                    variant="outline"
                    className="w-full mt-2 rounded-full font-semibold border-[#1D4053]/30 text-[#0D1830] hover:bg-[#72A4BF]/10"
                    data-testid="analyze-all-now-btn"
                  >
                    {loading ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Layers className="w-4 h-4 mr-2" />
                    )}
                    Analyze All Now (Parallel)
                  </Button>
                </div>
              )}
            </div>
          )}

          {/* QUEUE TAB */}
          {activeTab === 'queue' && (
            <div>
              {processingCount > 0 && (
                <div className="mb-4 p-3 rounded-lg bg-[#72A4BF]/10 border border-[#1D4053]/20">
                  <div className="flex items-center gap-2 mb-2">
                    <Loader2 className="w-4 h-4 text-[#0D1830] animate-spin" />
                    <span className="text-sm font-medium text-[#0D1830]">Processing {processingCount} project{processingCount > 1 ? 's' : ''}...</span>
                  </div>
                  <Progress value={33} className="h-1.5 bg-[#1D4053]/20 [&>div]:bg-[#1D4053]" />
                </div>
              )}

              {queue.filter(q => ['queued', 'processing'].includes(q.status)).length === 0 ? (
                <div className="py-12 text-center">
                  <ListOrdered className="w-10 h-10 text-[#1D4053]/30 mx-auto mb-3" />
                  <p className="text-sm text-[#72A4BF]">Queue is empty</p>
                  <p className="text-xs text-[#72A4BF]/60 mt-1">Select projects to queue for analysis</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {queue.filter(q => ['queued', 'processing'].includes(q.status)).map((item, idx) => {
                    const sc = statusConfig[item.status];
                    const Icon = sc.icon;
                    return (
                      <div
                        key={item.queue_id}
                        className="flex items-center gap-3 p-3 rounded-lg border border-[#E2E8ED] bg-white"
                      >
                        <span className="w-6 h-6 rounded-full bg-[#F0F4F7] text-xs font-bold flex items-center justify-center text-[#446E87]">
                          {idx + 1}
                        </span>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-[#0D1830] truncate">{item.project_name}</p>
                          <p className="text-xs text-[#72A4BF]">Queued {new Date(item.queued_at).toLocaleTimeString()}</p>
                        </div>
                        <Badge className={`${sc.color} border-0 text-[10px]`}>
                          <Icon className={`w-3 h-3 mr-1 ${item.status === 'processing' ? 'animate-spin' : ''}`} />
                          {sc.label}
                        </Badge>
                        {item.status === 'queued' && (
                          <button
                            onClick={() => removeFromQueue(item.queue_id)}
                            className="p-1.5 rounded-lg hover:bg-red-50 text-red-400 hover:text-red-600"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* HISTORY TAB */}
          {activeTab === 'history' && (
            <div>
              {history.length === 0 ? (
                <div className="py-12 text-center">
                  <BarChart3 className="w-10 h-10 text-[#1D4053]/30 mx-auto mb-3" />
                  <p className="text-sm text-[#72A4BF]">No batch history yet</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {history.map((item) => {
                    const sc = statusConfig[item.status];
                    const Icon = sc.icon;
                    return (
                      <div
                        key={item.queue_id}
                        className="flex items-center gap-3 p-3 rounded-lg border border-[#E2E8ED] bg-white hover:bg-[#F7FAFB] cursor-pointer"
                        onClick={() => window.location.href = `/projects/${item.project_id}`}
                      >
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-[#0D1830] truncate">{item.project_name}</p>
                          <p className="text-xs text-[#72A4BF]">
                            Completed {item.completed_at ? new Date(item.completed_at).toLocaleString() : 'N/A'}
                          </p>
                        </div>
                        <Badge className={`${sc.color} border-0 text-[10px]`}>
                          <Icon className="w-3 h-3 mr-1" />
                          {sc.label}
                        </Badge>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
